import { AxiosError } from 'axios';
import { centsToDollar, getErrorMessage_v2, toTitleCase } from 'core/Utils';
import { useCancelUserSubscription, useShowUserSubscription } from 'store/Api';
import {
  ButtonMantine,
  FullWidthButtonGroup,
  InfoListCard,
  Modal,
  Stack,
  Title,
  notifications,
} from 'ui';

import {
  ModalTitleWithBackButton,
  useDonationSettingModalProvider,
} from '../DonationSettingsModalProvider';

type CancelDonationContentProps = {
  subscriptionId?: string;
  onSuccess: () => void;
  onCancel: () => void;
};

const CancelDonationContent = ({
  subscriptionId,
  onSuccess,
  onCancel,
}: CancelDonationContentProps) => {
  const { data: subscriptionData } = useShowUserSubscription(subscriptionId);
  const { mutate: cancelDonation, isPending: isLoading } = useCancelUserSubscription();

  const handleCancelSubscription = () => {
    if (!subscriptionId) return;
    cancelDonation(subscriptionId, {
      onSuccess() {
        notifications.show({
          message: 'Your recurring donation has been canceled',
          variant: 'success',
        });
        onSuccess();
      },
      onError(e) {
        const error = e as Error | AxiosError;
        notifications.show({
          message: getErrorMessage_v2(error),
          variant: 'error',
        });
      },
    });
  };

  return (
    <Stack>
      <Title order={4}>
        {`Are you sure you want to cancel your recurring donation to ${
          subscriptionData?.cause.metadata.name ?? 'your cause'
        }?`}
      </Title>
      <InfoListCard color="blue" direction="column" cols={1} enableUniformLabelWidths>
        <InfoListCard.Item
          label="Donation Amount:"
          detail={`${centsToDollar(subscriptionData?.price)} / month`}
        />
        <InfoListCard.Item
          label="Payment Method:"
          detail={`${toTitleCase(subscriptionData?.metadata.brand ?? '')} (${subscriptionData
            ?.metadata.last4})`}
        />
      </InfoListCard>
      <FullWidthButtonGroup>
        <ButtonMantine variant="subtle" onClick={() => onCancel()}>
          Cancel
        </ButtonMantine>
        <ButtonMantine color="cerise" loading={isLoading} onClick={handleCancelSubscription}>
          Cancel Donation
        </ButtonMantine>
      </FullWidthButtonGroup>
    </Stack>
  );
};
const CancelDonationModal = () => {
  const { opened, close, meta, openBack, openModalById } = useDonationSettingModalProvider({
    id: 'CancelDonation',
  });

  const { subscriptionId } = meta;

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<ModalTitleWithBackButton>Cancel Donation</ModalTitleWithBackButton>}
    >
      <CancelDonationContent
        subscriptionId={subscriptionId}
        onSuccess={() => openModalById('ManageRecurring')}
        onCancel={openBack}
      />
    </Modal>
  );
};

export { CancelDonationContent, CancelDonationModal };
